// src/seo/SeoHead.tsx
import { useEffect } from 'react';
import { applyHead } from './core/headManager';
import type { PageMetadata } from './types';

export interface SeoHeadProps {
  /** Built by one of the builders in `src/seo/core/metadata.js`. */
  metadata: PageMetadata;
  /** Optional JSON-LD graph nodes for this page. */
  jsonLd?: Record<string, unknown>[];
}

/**
 * The reusable metadata engine's React entry point — CRA's stand-in for
 * Next.js `generateMetadata`.
 *
 * Every public page renders exactly one `<SeoHead>` built from real page data.
 * It owns `<title>`, description, canonical, robots, Open Graph, Twitter and
 * JSON-LD for that URL, and it adopts (replaces) the tags the build-time
 * prerenderer already wrote into `<head>`, so a prerendered page never ends up
 * with a duplicate title or a second rel=canonical after the SPA boots.
 *
 * Renders nothing — the head is managed imperatively so there is exactly one
 * owner of these tags at any moment. See `core/headManager.js` for why no
 * head-management library was added.
 */
function SeoHead({ metadata, jsonLd }: SeoHeadProps): null {
  const canonical = metadata?.canonical;
  const title = metadata?.title;
  const robots = metadata?.robots;

  useEffect(() => {
    if (!metadata) return;
    applyHead(document, metadata, jsonLd || []);
    // `metadata`/`jsonLd` are rebuilt on every render by the page's model
    // builder, so we key the effect on the values that actually identify the
    // page rather than on object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical, title, robots, JSON.stringify(jsonLd || [])]);

  return null;
}

export default SeoHead;
